// License หมดอายุของ AP Lab (ProductList/PSC.isLabExpireDate) — ฝั่งเว็บ
//
// ฝาแฝดของ firebase/functions/src/libs/libLabLicense.js แก้ที่ไหนต้องแก้อีกที่
// ให้ตรงกัน เพราะเว็บกับ Cloud Functions ต้องตัดสิน "หมดอายุหรือยัง" ให้ได้
// คำตอบเดียวกันเสมอ ไม่งั้นจะเจออาการหน้าเว็บเข้าได้แต่ประมวลผลไฟล์ไม่ขึ้น
//
// รูปแบบที่เก็บคือ 'YYYY-MM-DD' ตามปฏิทินไทย license ใช้ได้ถึงสิ้นวันนั้น
// และเริ่มบล็อกวันถัดไป ค่าว่างหรือไม่มีฟิลด์ = ไม่มีวันหมดอายุ ใช้ได้ตลอด
// (บริษัท Lab ที่สร้างไว้ก่อนมีฟีเจอร์นี้จึงไม่ถูกตัดสิทธิ์ย้อนหลัง)
//
// ไม่ใช้ moment ที่นี่ เพราะ moment อ่านเวลาตามเครื่องผู้ใช้ — ทีมงานที่เปิด
// หน้าจอจากคนละ timezone จะได้ "วันนี้" คนละวันกับ Cloud Functions

// จำนวนเดือนที่ให้เป็นค่าเริ่มต้นตอนสร้างบริษัท Lab
export const LAB_LICENSE_DEFAULT_MONTHS = 1;

// ไทยไม่มี DST จึงบวก offset คงที่ได้
const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function pad2(n) {
  return String(n).padStart(2, "0");
}

// วันที่ของ "วันนี้" ตามปฏิทินไทย ในรูปแบบ 'YYYY-MM-DD'
export function bangkokToday(now) {
  const base = now instanceof Date ? now : new Date();
  return new Date(base.getTime() + BANGKOK_OFFSET_MS).toISOString().slice(0, 10);
}

// รับได้ทั้ง 'YYYY-MM-DD' และ ISO เต็ม คืน '' เมื่อไม่ใช่วันที่ที่ใช้ได้
export function normalizeLabExpireDate(value) {
  if (!value) return "";

  if (typeof value === "object" && typeof value.toDate === "function") {
    return bangkokToday(value.toDate());
  }
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    return bangkokToday(value);
  }

  const raw = String(value).trim();
  if (!raw) return "";

  const dateOnly = raw.slice(0, 10);
  if (!DATE_ONLY_PATTERN.test(dateOnly)) return "";

  // กันวันที่ที่ไม่มีอยู่จริง เช่น 2026-02-31
  const [year, month, day] = dateOnly.split("-").map(Number);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return "";
  }

  return dateOnly;
}

// บวกเดือนแบบ clamp ปลายเดือน: 31 ม.ค. + 1 เดือน = 28/29 ก.พ. ไม่ใช่ 2/3 มี.ค.
export function addMonthsToDateString(dateString, months) {
  const normalized = normalizeLabExpireDate(dateString);
  if (!normalized) return "";

  const [year, month, day] = normalized.split("-").map(Number);
  const targetIndex = month - 1 + Number(months || 0);
  const targetYear = year + Math.floor(targetIndex / 12);
  const targetMonth = ((targetIndex % 12) + 12) % 12;
  // day 0 ของเดือนถัดไป = วันสุดท้ายของเดือนเป้าหมาย
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth + 1, 0)
  ).getUTCDate();

  return `${targetYear}-${pad2(targetMonth + 1)}-${pad2(
    Math.min(day, lastDayOfTargetMonth)
  )}`;
}

// วันหมดอายุเริ่มต้นตอนสร้างบริษัท Lab = วันนี้ + LAB_LICENSE_DEFAULT_MONTHS
export function defaultLabExpireDate(now) {
  return addMonthsToDateString(bangkokToday(now), LAB_LICENSE_DEFAULT_MONTHS);
}

// หมดอายุเมื่อวันนี้ (ปฏิทินไทย) เลยวันที่กำหนดไปแล้ว — ใช้ได้ถึงสิ้นวันนั้น
export function isLabExpireDatePassed(expireDate, now) {
  const normalized = normalizeLabExpireDate(expireDate);
  if (!normalized) return false;
  return bangkokToday(now) > normalized;
}

// ใช้กับ ProductList/PSC ทั้งก้อน — บริษัทที่ไม่ใช่ Lab ไม่มีวันหมดอายุ
export function isLabLicenseExpired(productList, now) {
  if (!productList || productList.isLab !== true) return false;
  return isLabExpireDatePassed(productList.isLabExpireDate, now);
}

// จำนวนวันที่เหลือ (0 = หมดวันนี้, ติดลบ = เลยมาแล้ว, null = ไม่มีวันหมดอายุ)
export function labLicenseDaysLeft(expireDate, now) {
  const normalized = normalizeLabExpireDate(expireDate);
  if (!normalized) return null;
  const today = bangkokToday(now);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((Date.parse(`${normalized}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / msPerDay);
}

// แสดงเป็นวันที่ไทย เช่น 26 ก.ย. 2569 — คืน '' เมื่อไม่มีวันหมดอายุ
export function formatLabExpireDate(expireDate) {
  const normalized = normalizeLabExpireDate(expireDate);
  if (!normalized) return "";
  return new Date(`${normalized}T00:00:00Z`).toLocaleDateString("th-TH", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
